import React, { useState, useEffect } from "react";
import {
  Button,
  Grid,
  TextField,
  Typography,
  Card,
  CardHeader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Tooltip,
  IconButton,
  CardContent
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import {
  createSize,
  getAllSize,
  deleteSize,
  updateSize,
} from "../../State/Size/Action";
import { toast } from "react-toastify";
import { Add, Close, Delete, Edit, FileDownload, PictureAsPdf, Print } from "@mui/icons-material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { PermissionService } from "../../../config/permissionService";
import { getSelectedExportRows } from "../../../config/exportSelection";

const AddSize = () => {
  const { sizes } = useSelector((store) => store);
  const [sizeData, setSizeData] = useState({ name: "" });
  const [editingId, setEditingId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const dispatch = useDispatch();
  const [openFormModal, setOpenFormModal] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [selectedRowIds, setSelectedRowIds] = useState([]);

  useEffect(() => {
    dispatch(getAllSize());
  }, [dispatch]);

  const handleChange = (e) => {
    setSizeData({ ...sizeData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (sizeData.name.trim() === "") {
      toast.error("Please fill all the fields");
      return;
    }

    if (sizeData.name.length < 1 || sizeData.name.length > 25) {
      toast.error("Size name should be between 1 to 25 characters");
      return;
    }

    if (editingId) {
      dispatch(updateSize(editingId, sizeData));
    } else {
      dispatch(createSize(sizeData));
    }

    handleCancle();
  };

  const handleSizeUpdate = (item) => {
    setSizeData({ name: item.name });
    setEditingId(item.id);
    setOpenFormModal(true);
  };

  const handleSizeDelete = (id) => {
    setSelectedId(id);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = () => {
    dispatch(deleteSize(selectedId));
    setOpenDeleteDialog(false);
    setSelectedId(null);
  };

  const handleCancle = () => {
    setSizeData({
      name: ""
    });
    setEditingId(null);
    setOpenFormModal(false);
  }

  const writeAction = PermissionService.has({ category: "Admin", page: "Sizes", type: "WRITE" });

  const columns = [
    {
      field: "serialNo",
      headerName: "S.No.",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1
    },
    { field: "name", headerName: "Size Name", flex: 1 },
    ...(writeAction ? [
      {
        field: "actions",
        headerName: "Action",
        width: 150,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box display="flex" gap={0} sx={{ p: 0, m: 0, minHeight: 0 }}>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                sx={{ p: 0, m: 0, minWidth: 24, minHeight: 24, cursor: "pointer" }}
                onClick={() => handleSizeUpdate(params.row)}
              >
                <Edit fontSize="small" sx={{ fontSize: 15, cursor: "pointer" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                color="error"
                size="small"
                sx={{ p: 0, m: 0, minWidth: 24, minHeight: 24, cursor: "pointer" }}
                onClick={() => handleSizeDelete(params.row.id)}
              >
                <Delete fontSize="small" sx={{ fontSize: 15, cursor: "pointer" }} />
              </IconButton>
            </Tooltip>
          </Box>
        )
      },
    ] : []),
  ];

  const rows =
    sizes?.sizes?.response?.map((item, index) => ({
      id: item.id,
      name: item.name,
    })) || [];

  const filteredRows = rows?.filter(row => {
    const query = searchText.toLowerCase();
    return (
      row.name?.toLowerCase().includes(query.toLowerCase())
    );
  });

    const getExportData = () => getSelectedExportRows(filteredRows, selectedRowIds);

  const handleExportCSV = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const csvContent = exportRows.map((row, index) => [index + 1, row.name].join(',')).join('\n');
    const headers = ['S.No.', 'Size Name'].join(',');
    const csv = `${headers}\n${csvContent}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'sizes.csv';
    link.click();
  };
  const handleExportPDF = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const doc = new jsPDF();
    doc.setFontSize(12);
    doc.text("Size List", 14, 15);
    const tableColumn = ['S.No.', 'Size Name'];
    const tableRows = exportRows.map((row, index) => [index + 1, row.name]);
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      theme: 'striped',
      headStyles: {
        fillColor: [37, 99, 235], // Tailwind blue-600 (RGB: 37,99,235)
        textColor: [255, 255, 255], // white text
        halign: 'left',
      },
      styles: { fontSize: 8, cellPadding: 1, overflow: 'linebreak' },
    });
    doc.save('sizes.pdf');
  };
  const handlePrint = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const printWindow = window.open('', '_blank');
    const headers = ['S.No.', 'Size Name'];
    const tableContent = exportRows.map((row, index) => [index + 1, row.name]);
    const html = `
                <html>
                  <head>
                    <title>Size List</title>
                    <style>
                      table { border-collapse: collapse; width: 100%; }
                      th, td { border: 1px solid black; padding: 8px; text-align: left; }
                      th {background: #449AEF;
                color: white;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact; }
                      tr:nth-child(even) { background-color: #f5f5f5; }
                    </style>
                  </head>
                  <body>
                    <h2>Size List</h2>
                    <table>
                      <thead>
                        <tr>
                          ${headers.map(header => `<th>${header}</th>`).join('')}
                        </tr>
                      </thead>
                      <tbody>
                        ${tableContent.map(row => `
                          <tr>
                            ${row.map(cell => `<td>${cell}</td>`).join('')}
                          </tr>
                        `).join('')}
                      </tbody>
                    </table>
                  </body>
                </html>
              `;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };


  return (
    <div >
      <Card className=" p-3" sx={{ borderRadius: 2, boxShadow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <CardHeader
            title={<Typography variant="h6" className="text-gray-700">SIZES</Typography>}
            sx={{ p: 0 }}
          />
          {writeAction && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                sx={{
                  background: "linear-gradient(90deg, #449AEF 0%, #f87171 100%)",
                  color: "#fff",
                  boxShadow: "0 2px 8px rgba(239,68,68,0.15)",
                  transition: "all 0.2s",
                  "&:hover": {
                    background: "linear-gradient(90deg, #f87171 0%, #449AEF 100%)",
                    boxShadow: "0 4px 16px rgba(239,68,68,0.25)",
                    transform: "scale(1.02)",
                  },
                  cursor: "pointer",
                  p: 1,
                  borderRadius: '50%',
                }}
                onClick={() => {
                  setOpenFormModal(true);
                  setEditingId(null);
                }}
              >
                <Add />
              </IconButton>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: "#449AEF", fontSize: "1.2rem", letterSpacing: 0.2 }}
              >
                Add Size
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5, mb: { xs: 2, sm: 0 } }}>
            <Button
              variant="contained"
              startIcon={<FileDownload />}
              onClick={handleExportCSV}
              sx={{
                background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                borderRadius: '6px',
                px: 2,
                py: 0.8,
                transition: 'all 0.2s',
                '&:hover': {
                  background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                  boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                  transform: 'scale(1.02)',
                },
                cursor: 'pointer',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Export CSV
            </Button>
            <Button
              variant="contained"
              startIcon={<PictureAsPdf />}
              onClick={handleExportPDF}
              sx={{
                background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                borderRadius: '6px',
                px: 2,
                py: 0.8,
                transition: 'all 0.2s',
                '&:hover': {
                  background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                  boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                  transform: 'scale(1.02)',
                },
                cursor: 'pointer',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Export PDF
            </Button>
            <Button
              variant="contained"
              startIcon={<Print />}
              onClick={handlePrint}
              sx={{
                background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                borderRadius: '6px',
                px: 2,
                py: 0.8,
                transition: 'all 0.2s',
                '&:hover': {
                  background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                  boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                  transform: 'scale(1.02)',
                },
                cursor: 'pointer',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Print
            </Button>
          </Box>
          <Box>
            <TextField
              label="Search Sizes"
              variant="outlined"
              size="small"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              sx={{ width: { xs: '100%', sm: '300px' } }}
            />
          </Box>
        </Box>
        <Box sx={{ height: "100%", width: "100%" }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            pagination
            initialState={{
              pagination: { paginationModel: { pageSize: 10, page: 0 } },
            }}
            pageSizeOptions={[10, 25, 50, 75, 100]}
            getRowId={(row) => row.id}
            checkboxSelection
            onRowSelectionModelChange={(ids) => setSelectedRowIds(ids)}
            rowSelectionModel={selectedRowIds}
            disableSelectionOnClick
            density="compact"
            experimentalFeatures={{ virtualization: true }}
            loading={sizes.loading}
            autoHeight={true}
            sx={{
              borderRadius: 2,
              fontSize: "0.8rem",
              "& .MuiDataGrid-cell": {
                fontSize: "0.8rem",
                border: "1px solid rgb(204, 201, 201)",
                minHeight: "36px !important",
                height: "36px !important",
                lineHeight: "1.2",
                paddingTop: "4px",
                paddingBottom: "4px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: "#449AEF",
                color: "#fff",
                fontWeight: "bold",
                fontSize: "0.8rem",
                padding: "8px",
                border: "1px solid rgb(204, 201, 201)",
                minHeight: "36px !important",
                height: "36px !important",
                lineHeight: "1.2",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontSize: "0.8rem",
                fontWeight: 600,
              },
              "& .MuiDataGrid-row": {
                maxHeight: "36px !important",
                minHeight: "36px !important",
              },
              "& .MuiDataGrid-row:nth-of-type(odd)": {
                backgroundColor: "#f5f5f5",
              },
              "& .MuiDataGrid-row:nth-of-type(even)": {
                backgroundColor: "#ffffff",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          />
        </Box>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this size?</DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Replace the form rendering with a Dialog: */}
      <Dialog
        open={openFormModal}
        onClose={handleCancle}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '1.25rem',
            fontWeight: 'bold',
            mt: 1,
            mb: 0.5,
            pb: 1,
            borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
          }}
        >
          {editingId ? "UPDATE SIZE" : "ADD NEW SIZE"}
          <IconButton onClick={handleCancle} size="small" sx={{ ml: 2 }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 2, py: 1 }}>
          <Card className="mb-4 mt-4" sx={{ borderRadius: 2, boxShadow: 1 }}>
            <CardContent sx={{ p: 2 }}>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={12}>
                    <TextField
                      fullWidth
                      label="Size Name"
                      name="name"
                      value={sizeData.name}
                      onChange={handleChange}
                      variant="outlined"
                      size="medium"
                    />
                  </Grid>

                  <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", gap: 2, }}>
                    <Button
                      variant="contained"
                      type="submit"
                      sx={{
                        background: "linear-gradient(90deg, #449AEF 0%, #f87171 100%)",
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        borderRadius: "6px",
                        boxShadow: "0 2px 8px rgba(239,68,68,0.15)",
                        px: 2.5,
                        py: 0.8,
                        transition: "all 0.2s",
                        "&:hover": {
                          background: "linear-gradient(90deg, #f87171 0%, #449AEF 100%)",
                          boxShadow: "0 4px 16px rgba(239,68,68,0.25)",
                          transform: "scale(1.02)",
                        },
                        width: { xs: "100%", sm: "auto" },
                        textAlign: "center"
                      }}
                    >
                      {editingId ? "Update size" : "Add size"}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleCancle}
                      sx={{
                        color: "#449AEF",
                        borderColor: "#449AEF",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        borderRadius: "6px",
                        px: 2.5,
                        py: 0.8,
                        transition: "all 0.2s",
                        "&:hover": {
                          backgroundColor: "#449AEF",
                          color: "#fff",
                          borderColor: "#449AEF",
                          boxShadow: "0 2px 8px rgba(239,68,68,0.1)",
                        },
                        width: { xs: "100%", sm: "auto" },
                        textAlign: "center"
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>

                </Grid>
              </form>
            </CardContent>
          </Card>
        </DialogContent>
      </Dialog>


    </div>
  );
};

export default AddSize;

